import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTour } from './context';

/** Breathing room between the spotlight edge and the element it lights. */
const PAD = 8;
/** Gap between the spotlight and the tooltip. */
const GAP = 12;
/** Keep the tooltip this far from the viewport edges. */
const MARGIN = 12;
/** Frames to wait for an anchor before giving up and skipping the step (~1.5s at 60fps). */
const MAX_WAIT_FRAMES = 90;

interface Box { top: number; left: number; width: number; height: number }
interface TipPos { top: number; left: number; caret: number; above: boolean }

/** Smallest box containing every match, so a step can light a button and its open menu as one shape. */
function unionRect(els: HTMLElement[]): Box {
  let top = Infinity, left = Infinity, right = -Infinity, bottom = -Infinity;
  for (const el of els) {
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) continue;
    top = Math.min(top, r.top);
    left = Math.min(left, r.left);
    right = Math.max(right, r.right);
    bottom = Math.max(bottom, r.bottom);
  }
  return { top, left, width: right - left, height: bottom - top };
}

/** Place the tooltip below the spotlight, flipping above when it would run off the bottom. */
function layout(box: Box, tip: HTMLElement | null): TipPos {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const h = tip?.offsetHeight ?? 180;
  const w = tip?.offsetWidth ?? 400; // keep in sync with .tour-tip width
  const belowTop = box.top + box.height + PAD + GAP;
  const aboveTop = box.top - PAD - GAP - h;
  const above = belowTop + h > vh - MARGIN && aboveTop > MARGIN;
  const centerX = box.left + box.width / 2;
  const left = Math.min(Math.max(centerX - w / 2, MARGIN), Math.max(MARGIN, vw - w - MARGIN));
  return {
    top: above ? aboveTop : belowTop,
    left,
    caret: Math.min(Math.max(centerX - left, 20), Math.max(20, w - 20)),
    above,
  };
}

/**
 * Spotlight + tooltip for the active tour step. Geometry is re-measured on an animation frame loop
 * rather than via scroll/resize listeners, which keeps the highlight glued to its target through
 * sticky-header scrolling, dropdowns opening, and font/layout settling — with state updates only
 * when the rounded geometry actually changes.
 */
export function TourOverlay() {
  const { step, index, total, next, back, stop, skip } = useTour();
  const [box, setBox] = useState<Box | null>(null);
  const [tip, setTip] = useState<TipPos | null>(null);
  const [missing, setMissing] = useState(false);
  const tipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!step) {
      setBox(null);
      setTip(null);
      setMissing(false);
      return;
    }
    setMissing(false);
    const anchor = step.anchor;
    let frame = 0;
    let waited = 0;
    let scrolled = false;
    let lastKey = '';

    const loop = () => {
      if (!anchor) {
        if (lastKey !== 'centered') {
          lastKey = 'centered';
          setBox(null);
          setTip(null);
        }
        frame = requestAnimationFrame(loop);
        return;
      }
      const els = Array.from(document.querySelectorAll<HTMLElement>(anchor))
        .filter((el) => el.getClientRects().length > 0);
      if (els.length === 0) {
        // Role-gated or removed element: wait briefly (routes/menus may still be mounting), then skip.
        if (++waited > MAX_WAIT_FRAMES) {
          setMissing(true);
          return;
        }
        frame = requestAnimationFrame(loop);
        return;
      }
      waited = 0;
      if (!scrolled) {
        scrolled = true;
        els[0]?.scrollIntoView({ block: 'center', inline: 'nearest' });
      }
      const rect = unionRect(els);
      const pos = layout(rect, tipRef.current);
      const key = [rect.top, rect.left, rect.width, rect.height, pos.top, pos.left, pos.above]
        .map((v) => (typeof v === 'number' ? Math.round(v) : v))
        .join('|');
      if (key !== lastKey) {
        lastKey = key;
        setBox(rect);
        setTip(pos);
      }
      frame = requestAnimationFrame(loop);
    };

    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
  }, [step]);

  // A step we can't anchor is dropped rather than shown floating in the middle of the screen.
  useEffect(() => {
    if (missing) skip();
  }, [missing, skip]);

  useEffect(() => {
    if (!step) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') stop(false);
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') back();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [step, stop, next, back]);

  // Move focus into the tooltip on each step so keyboard and screen-reader users follow along.
  useEffect(() => {
    if (step) tipRef.current?.focus();
  }, [step]);

  if (!step) return null;
  const centered = !step.anchor;
  const last = index === total - 1;

  return createPortal(
    <>
      {/* Swallows clicks so the dimmed app can't be operated mid-tour. */}
      <div className="tour-blocker" />
      {centered ? (
        <div className="tour-dim" />
      ) : (
        box && (
          <div
            className="tour-spot"
            style={{
              top: box.top - PAD,
              left: box.left - PAD,
              width: box.width + PAD * 2,
              height: box.height + PAD * 2,
            }}
          />
        )
      )}
      <div
        ref={tipRef}
        className={`tour-tip${centered ? ' is-centered' : ''}${tip?.above ? ' is-above' : ''}`}
        style={centered ? undefined : { top: tip?.top ?? -9999, left: tip?.left ?? -9999 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tour-tip-title"
        tabIndex={-1}
      >
        {!centered && <span className="tour-caret" style={{ left: tip?.caret ?? 20 }} aria-hidden />}
        <div className="tour-step-count">Step {index + 1} of {total}</div>
        <h3 id="tour-tip-title">{step.title}</h3>
        <p>{step.body}</p>
        <div className="tour-actions">
          <button type="button" className="btn btn-ghost btn-sm tour-skip" onClick={() => stop(false)}>
            Skip tour
          </button>
          <div className="tour-dots" aria-hidden>
            {Array.from({ length: total }, (_, i) => (
              <span key={i} className={`tour-dot${i === index ? ' is-on' : ''}`} />
            ))}
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={back} disabled={index === 0}>
            Back
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={next}>
            {last ? 'Done' : 'Next'}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}
